import {
  IssueTracker,
  type CallNoteMetadata,
  type CatalogueFeature,
  type DiscussionTopicArtifact,
  type HydratedEvidenceDocument,
  type Signal,
  type SignalArtifact,
} from "@devplaninc/codeup"
import {catalogueFeatureHref, catalogueFeatureLabel} from "@/app/util/catalogueFeatureUtil"
import {callNoteProviderKey, documentEvidenceKey} from "@/app/components/evidence/evidenceHydrationRefs"

type ProviderAwareDiscussionTopicArtifact = DiscussionTopicArtifact & {
  provider?: string
  permalink?: string
}

/**
 * Stable identifier for the source kind of a Signal — derived from the
 * dominant SignalArtifact. Used by every UI that renders signals (workspace
 * list, project feed, home page, …) so labels/colors/icons stay consistent.
 */
export type SignalSourceKind =
  | "call_note"
  | "discussion_topic"
  | "document"
  | "feature"
  | "jira"
  | "linear"
  | "ticket"
  | "event"

export interface SignalArtifactResolvers {
  featureMap?: Map<string, CatalogueFeature>
  callNotesByProviderKey?: Record<string, CallNoteMetadata>
  documents?: Record<string, HydratedEvidenceDocument>
  companyId?: number
}

export const SIGNAL_SOURCE_LABELS: Record<SignalSourceKind, string> = {
  call_note: "Call note",
  discussion_topic: "Discussion",
  document: "Document",
  feature: "Feature",
  jira: "Jira",
  linear: "Linear",
  ticket: "Ticket",
  event: "Event",
}

/** Canonical display order for filter chips that surface signal source kinds. */
export const SIGNAL_SOURCE_ORDER: SignalSourceKind[] = [
  "call_note",
  "discussion_topic",
  "jira",
  "linear",
  "ticket",
  "feature",
  "document",
  "event",
]

export function signalSourceLabel(source: SignalSourceKind): string {
  return SIGNAL_SOURCE_LABELS[source] ?? "Signal"
}

export function signalArtifactKind(artifact: SignalArtifact): SignalSourceKind {
  const details = artifact.details
  if (!details) return "event"
  switch (details.$case) {
    case "callNote":
      return "call_note"
    case "discussionTopic":
      return "discussion_topic"
    case "document":
      return "document"
    case "feature":
      return "feature"
    case "issue":
      switch (details.value.tracker) {
        case IssueTracker.ISSUE_TRACKER_JIRA:
          return "jira"
        case IssueTracker.ISSUE_TRACKER_LINEAR:
          return "linear"
        default:
          return "ticket"
      }
    default:
      // Unknown future `$case` value — fall back to "event" so we stay
      // assignable to `SignalSourceKind` without dropping signals entirely.
      return "event"
  }
}

/**
 * Pick the most frequent source kind across a signal's artifacts. Tiebreaks
 * on first appearance. Falls back to "event" when the signal has no
 * recognizable artifacts.
 */
export function pickDominantSignalSource(signal: Signal): SignalSourceKind {
  const counts = new Map<SignalSourceKind, {count: number; firstIndex: number}>()
  signal.artifacts.forEach((artifact, index) => {
    const kind = signalArtifactKind(artifact)
    const existing = counts.get(kind)
    if (existing) {
      existing.count += 1
    } else {
      counts.set(kind, {count: 1, firstIndex: index})
    }
  })
  let bestKind: SignalSourceKind | undefined
  let bestStat: {count: number; firstIndex: number} | undefined
  for (const [kind, stat] of counts) {
    if (
      !bestStat
      || stat.count > bestStat.count
      || (stat.count === bestStat.count && stat.firstIndex < bestStat.firstIndex)
    ) {
      bestKind = kind
      bestStat = stat
    }
  }
  return bestKind ?? "event"
}

/**
 * Human-readable provider label for a single SignalArtifact — e.g. "Granola"
 * for a callNote with providerType="granola", "Jira" for a Jira issue.
 */
export function artifactProviderLabel(artifact: SignalArtifact): string {
  const details = artifact.details
  if (!details) return signalSourceLabel("event")
  switch (details.$case) {
    case "callNote":
      return providerTypeLabel(details.value.providerType) || signalSourceLabel("call_note")
    case "discussionTopic":
      return discussionTopicProviderLabel(discussionTopicArtifactProvider(artifact))
    case "issue":
      switch (details.value.tracker) {
        case IssueTracker.ISSUE_TRACKER_JIRA:
          return signalSourceLabel("jira")
        case IssueTracker.ISSUE_TRACKER_LINEAR:
          return signalSourceLabel("linear")
        default:
          return signalSourceLabel("ticket")
      }
    case "feature":
      return signalSourceLabel("feature")
    case "document":
      return documentArtifactLabel(details.value.provider)
    default:
      return signalSourceLabel("event")
  }
}

/**
 * External URL associated with the artifact, when the source carries one
 * directly. Chat-backed discussion topics expose a provider-native permalink;
 * other kinds construct their URLs elsewhere. Returns `undefined` when no link
 * is available so callers can render text-only as a fallback.
 */
export function artifactReferenceHref(artifact: SignalArtifact, resolvers?: SignalArtifactResolvers): string | undefined {
  const details = artifact.details
  if (!details) return undefined
  switch (details.$case) {
    case "callNote":
      return callNoteHref(resolvers?.callNotesByProviderKey?.[callNoteProviderKey(details.value.providerType, details.value.noteId)])
    case "discussionTopic": {
      const topic = details.value as ProviderAwareDiscussionTopicArtifact
      const link = topic.permalink?.trim() || topic.slackPermalink?.trim()
      return link ? link : undefined
    }
    case "feature": {
      const featureId = details.value.featureId
      return catalogueFeatureHref(resolvers?.companyId, featureId)
    }
    case "document": {
      const doc = resolvers?.documents?.[documentEvidenceKey(details.value)]
      return doc?.url?.trim() || undefined
    }
    default:
      return undefined
  }
}

export function discussionTopicArtifactProvider(artifact: SignalArtifact): "slack" | "teams" | undefined {
  if (artifact.details?.$case !== "discussionTopic") return undefined
  const topic = artifact.details.value as ProviderAwareDiscussionTopicArtifact
  switch (topic.provider?.trim().toLowerCase() ?? "") {
    case "teams":
      return "teams"
    case "slack":
      return "slack"
    case "": {
      const legacyPermalink = topic.permalink?.trim() || topic.slackPermalink?.trim()
      return legacyPermalink?.includes("teams.microsoft.com/") ? "teams" : "slack"
    }
    default:
      return undefined
  }
}

function discussionTopicProviderLabel(provider: "slack" | "teams" | undefined): string {
  switch (provider) {
    case "teams":
      return "Microsoft Teams"
    case "slack":
      return "Slack"
    default:
      return signalSourceLabel("discussion_topic")
  }
}

/** Free-form reference text for a SignalArtifact (issue id, note title, …). */
export function artifactReferenceText(artifact: SignalArtifact, resolvers?: SignalArtifactResolvers): string {
  const details = artifact.details
  if (!details) return ""
  switch (details.$case) {
    case "callNote":
      return details.value.title.trim() || callNoteTitle(resolvers?.callNotesByProviderKey?.[callNoteProviderKey(details.value.providerType, details.value.noteId)]) || ""
    case "feature": {
      const featureId = details.value.featureId
      return catalogueFeatureLabel(resolvers?.featureMap?.get(featureId)) ?? ""
    }
    case "issue":
      return details.value.title.trim() || details.value.issueId || ""
    case "discussionTopic":
      return details.value.title.trim() || ""
    case "document": {
      const title = resolvers?.documents?.[documentEvidenceKey(details.value)]?.title?.trim() || ""
      return title && title !== details.value.documentId.trim() ? title : ""
    }
    default:
      return ""
  }
}

function providerTypeLabel(providerType: string): string {
  const trimmed = providerType.trim()
  if (!trimmed) return ""
  switch (trimmed.toLowerCase()) {
    case "other":
      return ""
    case "teams_meeting":
      return "Microsoft Teams"
  }
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase()
}

export function callNoteTitle(note: CallNoteMetadata | undefined): string {
  const details = note?.noteDetails
  if (details?.$case === "granola") return details.value.title.trim()
  if (details?.$case === "googleDrive") return details.value.title.trim()
  if (details?.$case === "teamsMeeting") return details.value.title.trim()
  if (details?.$case === "zoom") return details.value.title.trim()
  return ""
}

export function callNoteHref(note: CallNoteMetadata | undefined): string | undefined {
  const details = note?.noteDetails
  const url = details?.$case === "granola" || details?.$case === "googleDrive" || details?.$case === "teamsMeeting" || details?.$case === "zoom"
    ? callNoteDetailsUrl(details.value)
    : ""
  return url || undefined
}

function callNoteDetailsUrl(details: object): string {
  const withUrl = details as Record<string, unknown>
  const url = typeof withUrl.webUrl === "string"
    ? withUrl.webUrl
    : typeof withUrl.web_url === "string" ? withUrl.web_url : ""
  return url.trim()
}

export function documentArtifactLabel(provider: string): string {
  switch (provider.trim().toLowerCase()) {
    case "upload":
    case "uploaded":
      return "Uploaded document"
    case "drive":
    case "google_drive":
    case "google-drive":
      return "Google Docs"
    case "notion":
      return "Notion page"
    case "confluence":
      return "Confluence page"
    default:
      return signalSourceLabel("document")
  }
}
